import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { get } from 'lodash';
import { S3Image, getAvatarStyle } from '../../../../lib/s3';
import DocumentAttachment from './DocumentAttachment';
import { formatMessageTime, formatAIChatTime } from '../shared/dateUtils';

/**
 * MessageItem - Beautiful message display component with proper grouping
 * 
 * Features proper user identification, message grouping with dividers, and generous negative space
 */
const MessageItem = ({ 
  message, 
  currentUser, 
  isFirst, 
  showTime, 
  otherParticipant, 
  conversation,
  isHighlighted = false,
  isLastInGroup = false,
  showDivider = false,
  nextMessageSender = null,
  onReaction = null,
  companySettings = {}
}) => {
  const intl = useIntl();
  // Detect Paddy messages based on ChatWidget logic
  const isPaddyMessage = !message.sender?.id && 
    (['paddy', 'support_request'].includes(conversation?.conversationType));
  
  // For partial sale conversations, don't treat as Paddy messages
  const isPartialSaleMessage = !message.sender?.id && 
    conversation?.conversationType === 'partial_sale_listing';
  
  // Gate for the RAW HTML sink only — deliberately stricter than the two
  // predicates above, which also drive the avatar, display name and bubble
  // styling and are left alone.
  //
  // `!message.sender?.id` is an ABSENCE check on an association that resolves to
  // null for two different reasons: a genuinely app-authored Paddy message, and a
  // user-authored message whose sender row no longer resolves. The second is a
  // stored-XSS vector (Cobalt PT41947_6, D1). `system_authored` is computed from
  // the sender COLUMN server-side (personal_message_type.rb) and separates them.
  //
  // Fail-closed by construction: any surface that does not request the field
  // gets `undefined`, which renders the body as escaped text rather than HTML.
  const isSystemAuthoredHtml = (isPaddyMessage || isPartialSaleMessage) &&
    message.system_authored === true;

  const isOwnMessage = !isPaddyMessage && !isPartialSaleMessage && String(message.sender?.id) === String(currentUser?.id);
  
  // Determine sender name for partial sales vs other conversations
  let senderName;
  if (isPaddyMessage) {
    senderName = 'Paddy';
  } else if (isPartialSaleMessage && conversation?.partialSaleListingUser) {
    // For partial sale system messages, show the appropriate person's name
    const { buyer, seller } = conversation.partialSaleListingUser;
    if (String(currentUser?.id) === String(seller?.id)) {
      senderName = buyer?.allowed_name || buyer?.name || 'Buyer';
    } else if (String(currentUser?.id) === String(buyer?.id)) {
      senderName = seller?.allowed_name || seller?.name || 'Seller';
    } else {
      senderName = 'System';
    }
  } else {
    senderName = message.sender?.allowed_name || message.sender?.name || 'Unknown';
  }
  
  // Get the profile picture - student_avatar_url is a full URL, profile_picture is an S3 filename
  let senderProfilePicture;
  let senderAvatarIsFullUrl = false;
  if (isPaddyMessage) {
    senderProfilePicture = 'paddy.png';
  } else if (isPartialSaleMessage && conversation?.partialSaleListingUser) {
    const { buyer, seller } = conversation.partialSaleListingUser;
    if (String(currentUser?.id) === String(seller?.id)) {
      senderProfilePicture = buyer?.profile_picture || 'advisor.png';
    } else if (String(currentUser?.id) === String(buyer?.id)) {
      senderProfilePicture = seller?.profile_picture || 'advisor.png';
    } else {
      senderProfilePicture = 'advisor.png';
    }
  } else if (message.sender?.student_avatar_url) {
    senderProfilePicture = message.sender.student_avatar_url;
    senderAvatarIsFullUrl = true;
  } else {
    senderProfilePicture = message.sender?.profile_picture || 'advisor.png';
  }
  
  // Using Safari-compatible formatMessageTime from dateUtils

  // Generate avatar gradient
  const getAvatarGradient = (name) => {
    const gradients = [
      'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
      'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
      'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
      'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
      'linear-gradient(135deg, #fad0c4 0%, #ffd1ff 100%)'
    ];
    
    const charCode = name.charCodeAt(0) || 0;
    return gradients[charCode % gradients.length];
  };

  // Rich message rendering with links, mentions, and formatting
  const renderRichMessage = (text) => {
    if (!text) return '';

    // URL regex pattern
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    // Email regex pattern
    const emailRegex = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
    // Mention regex pattern (@username)
    const mentionRegex = /(@\w+)/g;
    
    let parts = [text];
    
    // Process URLs
    parts = parts.flatMap(part => {
      if (typeof part !== 'string') return part;
      return part.split(urlRegex).map((segment, index) => {
        if (urlRegex.test(segment)) {
          return (
            <a
              key={`url-${index}`}
              href={segment}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: 'var(--succession-primary)',
                textDecoration: 'none',
                fontWeight: '500',
                borderBottom: '1px solid var(--succession-primary)',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'var(--succession-primary-10)';
                e.target.style.borderRadius = '4px';
                e.target.style.padding = '2px 4px';
                e.target.style.margin = '0 -4px';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.borderRadius = '0';
                e.target.style.padding = '0';
                e.target.style.margin = '0';
              }}
            >
              {segment}
            </a>
          );
        }
        return segment;
      });
    });

    // Process emails
    parts = parts.flatMap(part => {
      if (typeof part !== 'string') return part;
      return part.split(emailRegex).map((segment, index) => {
        if (emailRegex.test(segment)) {
          return (
            <a
              key={`email-${index}`}
              href={`mailto:${segment}`}
              style={{
                color: 'var(--succession-primary)',
                textDecoration: 'none',
                fontWeight: '500',
                borderBottom: '1px solid var(--succession-primary)',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'var(--succession-success-bg)';
                e.target.style.borderRadius = '4px';
                e.target.style.padding = '2px 4px';
                e.target.style.margin = '0 -4px';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.borderRadius = '0';
                e.target.style.padding = '0';
                e.target.style.margin = '0';
              }}
            >
              {segment}
            </a>
          );
        }
        return segment;
      });
    });

    // Process mentions
    parts = parts.flatMap(part => {
      if (typeof part !== 'string') return part;
      return part.split(mentionRegex).map((segment, index) => {
        if (mentionRegex.test(segment)) {
          return (
            <span
              key={`mention-${index}`}
              style={{
                color: 'var(--succession-accent)',
                backgroundColor: 'var(--succession-accent-bg)',
                padding: '2px 6px',
                borderRadius: '6px',
                fontWeight: '600',
                fontSize: '14px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'var(--succession-accent-20)';
                e.target.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'var(--succession-accent-bg)';
                e.target.style.transform = 'scale(1)';
              }}
            >
              {segment}
            </span>
          );
        }
        return segment;
      });
    });

    return parts.map((part, index) => (
      <React.Fragment key={index}>{part}</React.Fragment>
    ));
  };

  // Handle emoji reaction
  const handleReaction = (emoji) => {
    if (onReaction) {
      onReaction(message.id, emoji);
    } else {
      // You could show a toast notification here
      alert(`Reaction feature coming soon! You clicked ${emoji}`);
    }
  };

  // Demo-style clean message layout
  return (
    <>
      {/* CSS for Paddy message styling */}
      <style>
        {`
          .paddy-message-content b { font-weight: bold; }
          .paddy-message-content br { display: block; margin: 0.5em 0; }
          .paddy-message-content a { color: #4A90E2; text-decoration: none; }
          .paddy-message-content a:hover { text-decoration: underline; }
          .paddy-message-content .btn {
            display: inline-block;
            padding: 8px 16px;
            background-color: #4A90E2;
            color: white !important;
            border-radius: 6px;
            text-decoration: none !important;
            font-size: 14px;
            margin-top: 8px;
          }
          .message-bubble:hover .quick-reactions { opacity: 1; }
        `}
      </style>
      
      {/* Message row - Demo style */}
      <div className={`flex mb-2 ${isOwnMessage ? 'justify-end' : 'justify-start'} ${isHighlighted ? 'bg-yellow-50 rounded-lg' : ''}`}>
        <div className={`flex gap-3 max-w-[70%] ${isOwnMessage ? 'flex-row-reverse' : 'flex-row'}`}>
          {/* Avatar - only show for received messages on first in group */}
          {!isOwnMessage && isFirst && (
            <div className="relative w-10 h-10 flex-shrink-0">
              <div className="w-10 h-10 rounded-full overflow-hidden bg-[var(--succession-bg-tertiary)]">
                {senderProfilePicture ? (
                  <img
                    src={senderAvatarIsFullUrl ? senderProfilePicture : S3Image(senderProfilePicture)}
                    alt={senderName}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div className={`${senderProfilePicture ? 'hidden' : 'flex'} w-full h-full bg-[#4A90E2] items-center justify-center text-white font-medium text-sm`}>
                  {senderName.charAt(0).toUpperCase()}
                </div>
              </div>
              {message.sender?.is_student && (
                <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 text-[8px] font-bold leading-none px-1 py-0.5 rounded bg-blue-100 text-blue-700 whitespace-nowrap border border-white">
                  {intl.formatMessage({ id: 'succession.messaging.chat_panel.recruit_badge' })}
                </span>
              )}
            </div>
          )}
          
          {/* Spacer when avatar is hidden */}
          {!isOwnMessage && !isFirst && <div className="w-10 flex-shrink-0" />}

          {/* Message content */}
          <div className={`flex flex-col ${isOwnMessage ? 'items-end' : 'items-start'}`}>
            {/* Message bubble - sent messages use primary color via CSS variable */}
            <div 
              className="message-bubble relative rounded-2xl px-4 py-2.5"
              style={
                isPaddyMessage || isPartialSaleMessage
                  ? { backgroundColor: '#F9FAFB', color: '#111827', border: '1px solid #E5E7EB' }
                  : isOwnMessage 
                    ? { backgroundColor: 'var(--succession-primary)', color: '#ffffff' }
                    : { backgroundColor: '#F3F4F6', color: '#111827' }
              }
            >
              {/* Message body */}
              <span className="text-sm leading-relaxed">
                {isSystemAuthoredHtml ? (
                  <span className="paddy-message-content" dangerouslySetInnerHTML={{ __html: message.body }} />
                ) : (
                  renderRichMessage(message.body)
                )}
              </span>
              
              {/* Document Attachments */}
              {message.documents && message.documents.length > 0 && (
                <div 
                  className="mt-2 pt-2"
                  style={{ borderTop: `1px solid ${isOwnMessage ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'}` }}
                >
                  {message.documents.map((document) => (
                    <DocumentAttachment key={document.id} document={document} showDownload={true} />
                  ))}
                </div>
              )}
              
              {/* Reactions */}
              {message.reactions && message.reactions.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {message.reactions.map((reaction, index) => (
                    <button
                      key={index}
                      onClick={() => handleReaction(reaction.emoji)}
                      className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs transition-colors"
                      style={{
                        backgroundColor: isOwnMessage ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.05)',
                        color: isOwnMessage ? '#ffffff' : '#374151'
                      }}
                    >
                      <span>{reaction.emoji}</span>
                      <span>{reaction.count}</span>
                    </button>
                  ))}
                </div>
              )}
              
              {/* Quick reactions on hover */}
              <div className={`quick-reactions absolute -top-8 ${isOwnMessage ? 'left-0' : 'right-0'} flex gap-1 bg-white rounded-full shadow-lg px-2 py-1 opacity-0 transition-opacity border border-[var(--succession-border)]`}>
                {['👍', '❤️', '😊'].map((emoji, index) => (
                  <button key={index} onClick={() => handleReaction(emoji)} className="hover:scale-110 transition-transform p-1">
                    {emoji}
                  </button>
                ))}
              </div>

              {/* Multiple Choice Options */}
              {message.messageType === 'multiple_choice' && message.choices && (
                <div className="flex flex-col gap-2 mt-2">
                  {message.choices?.map((choice, index) => (
                    <button 
                      key={index} 
                      className="px-3 py-2 rounded-lg text-sm text-left transition-colors"
                      style={{
                        backgroundColor: isOwnMessage ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.05)',
                        color: isOwnMessage ? '#ffffff' : '#374151'
                      }}
                    >
                      {choice}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Timestamp and status - Demo style with full date and time */}
            <div className={`flex items-center gap-1.5 mt-1 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
              <span className="text-xs" style={{ color: '#9CA3AF' }}>
                {formatAIChatTime(message.createdAt)}
              </span>
              {isOwnMessage && message.read && (
                <span className="text-xs" style={{ color: '#9CA3AF' }}>{intl.formatMessage({ id: 'succession.messaging.chat_panel.read_status' })}</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

MessageItem.propTypes = {
  message: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    body: PropTypes.string.isRequired,
    createdAt: PropTypes.string.isRequired,
    sender: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string,
      allowed_name: PropTypes.string,
      profile_picture: PropTypes.string
    }).isRequired,
    read: PropTypes.bool,
    messageType: PropTypes.string,
    choices: PropTypes.arrayOf(PropTypes.string),
    reactions: PropTypes.arrayOf(PropTypes.shape({
      emoji: PropTypes.string.isRequired,
      count: PropTypes.number.isRequired
    })),
    documents: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string,
          download_url: PropTypes.string,
    mime_type: PropTypes.string,
    created_at: PropTypes.string
    }))
  }).isRequired,
  currentUser: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string
  }).isRequired,
  conversation: PropTypes.shape({
    conversationType: PropTypes.string
  }),
  isFirst: PropTypes.bool.isRequired,
  showTime: PropTypes.bool,
  otherParticipant: PropTypes.object,
  isHighlighted: PropTypes.bool,
  isLastInGroup: PropTypes.bool,
  showDivider: PropTypes.bool,
  nextMessageSender: PropTypes.object,
  onReaction: PropTypes.func,
  companySettings: PropTypes.shape({
    new_avatars_enabled: PropTypes.bool,
    avatar_directory: PropTypes.string,
    avatar_background_color: PropTypes.string,
    primary_color: PropTypes.string,
  })
};

export default MessageItem; 